const API = '/api/v1'

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

async function handle(res) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    const detail = err.detail
    const message = Array.isArray(detail)
      ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
      : detail || err.message || `Request failed: ${res.status}`
    throw new Error(message)
  }
  if (res.status === 204) return null
  return res.json()
}

// Profile
export async function updateProfile(token, data) {
  return handle(await fetch(`/api/v1/auth/me`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

// Cart
export async function getCart(token) {
  return handle(await fetch(`${API}/cart`, { headers: authHeaders(token) }))
}

export async function addCartItem(token, item) {
  return handle(await fetch(`${API}/cart/items`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(item),
  }))
}

export async function updateCartItem(token, itemId, quantity) {
  return handle(await fetch(`${API}/cart/items/${itemId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ quantity }),
  }))
}

export async function removeCartItem(token, itemId) {
  return handle(await fetch(`${API}/cart/items/${itemId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  }))
}

export async function clearCartApi(token) {
  return handle(await fetch(`${API}/cart`, {
    method: 'DELETE',
    headers: authHeaders(token),
  }))
}

export async function validateCart(token) {
  return handle(await fetch(`${API}/cart/validate`, {
    method: 'POST',
    headers: authHeaders(token),
  }))
}

// Addresses
export async function getAddresses(token) {
  return handle(await fetch(`${API}/addresses`, { headers: authHeaders(token) }))
}

export async function createAddress(token, data) {
  return handle(await fetch(`${API}/addresses`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function updateAddress(token, id, data) {
  return handle(await fetch(`${API}/addresses/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function deleteAddress(token, id) {
  return handle(await fetch(`${API}/addresses/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  }))
}

// Orders
export async function createOrder(token, data) {
  return handle(await fetch(`${API}/orders`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function getOrders(token, { page = 1, per_page = 100, status } = {}) {
  const params = new URLSearchParams({ page, per_page })
  if (status) params.set('status', status)
  return handle(await fetch(`${API}/orders?${params}`, { headers: authHeaders(token) }))
}

export async function getOrder(token, id) {
  return handle(await fetch(`${API}/orders/${id}`, { headers: authHeaders(token) }))
}

export async function cancelOrder(token, id) {
  return handle(await fetch(`${API}/orders/${id}/cancel`, {
    method: 'POST',
    headers: authHeaders(token),
  }))
}

async function fetchAllDelivered(token, dateOpts) {
  let page = 1, items = [], total = Infinity
  while (items.length < total) {
    const res = await adminGetOrders(token, { status: 'delivered', per_page: 100, page, ...dateOpts })
    items = items.concat(res?.items ?? [])
    total = res?.total ?? 0
    if (!res?.items?.length) break
    page++
  }
  return items
}

// Admin stats — parallel per-status count + revenue from delivered orders
export async function adminGetOrderStats(token, { date_from, date_to } = {}) {
  const statuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled']
  const dateOpts = { date_from, date_to }
  const [results, deliveredItems] = await Promise.all([
    Promise.all(statuses.map(s => adminGetOrders(token, { status: s, per_page: 1, ...dateOpts }))),
    fetchAllDelivered(token, dateOpts),
  ])
  const by_status = Object.fromEntries(statuses.map((s, i) => [s, results[i]?.total ?? 0]))
  const active = (by_status.confirmed ?? 0) + (by_status.processing ?? 0) + (by_status.shipped ?? 0)
  const total_orders = Object.values(by_status).reduce((a, b) => a + b, 0)
  const total_revenue = deliveredItems.reduce((s, o) => s + Number(o.total), 0)
  return { by_status, active, total_orders, total_revenue }
}

export async function adminGetOrders(token, { page = 1, per_page = 20, status, user_id, date_from, date_to } = {}) {
  const params = new URLSearchParams({ page, per_page, user_id: user_id ?? 0 })
  if (status) params.set('status', status)
  if (date_from) params.set('date_from', date_from)
  if (date_to) params.set('date_to', date_to)
  return handle(await fetch(`${API}/orders?${params}`, { headers: authHeaders(token) }))
}

export async function adminUpdateOrderStatus(token, orderId, status) {
  return handle(await fetch(`${API}/orders/${orderId}/status`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ status }),
  }))
}

export async function adminUpdateOrder(token, orderId, data) {
  return handle(await fetch(`${API}/orders/${orderId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function adminCancelOrder(token, orderId) {
  return handle(await fetch(`${API}/orders/${orderId}/cancel`, {
    method: 'POST',
    headers: authHeaders(token),
  }))
}

export async function adminListUsers(token, { page = 1, size = 20, search, role, date_from, date_to } = {}) {
  const params = new URLSearchParams({ page, size })
  if (search) params.set('search', search)
  if (role) params.set('role', role)
  if (date_from) params.set('date_from', date_from)
  if (date_to) params.set('date_to', date_to)
  return handle(await fetch(`${API}/users?${params}`, { headers: authHeaders(token) }))
}

export async function adminGetUser(token, userId) {
  return handle(await fetch(`${API}/users/${userId}`, { headers: authHeaders(token) }))
}

export async function adminGetUserAddresses(token, userId) {
  return handle(await fetch(`${API}/addresses?user_id=${userId}`, { headers: authHeaders(token) }))
}

export async function adminGetUserCart(token, userId) {
  return handle(await fetch(`${API}/cart?user_id=${userId}`, { headers: authHeaders(token) }))
}

export async function adminPatchUser(token, userId, data) {
  return handle(await fetch(`${API}/users/${userId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function adminCreateCustomer(token, data) {
  return handle(await fetch(`${API}/users`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

// HAIST Admin — Shops (/api/v1/admin/shops, requires datium role)
export async function haistListShops(token, { page = 1, size = 20, search, is_active } = {}) {
  const params = new URLSearchParams({ page, size })
  if (search) params.set('search', search)
  if (is_active !== undefined && is_active !== null) params.set('is_active', is_active)
  return handle(await fetch(`${API}/admin/shops?${params}`, { headers: authHeaders(token) }))
}

export async function haistGetShop(token, shopId) {
  return handle(await fetch(`${API}/admin/shops/${shopId}`, { headers: authHeaders(token) }))
}

export async function haistCreateShop(token, data) {
  return handle(await fetch(`${API}/admin/shops`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function haistUpdateShop(token, shopId, data) {
  return handle(await fetch(`${API}/admin/shops/${shopId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function haistDeleteShop(token, shopId) {
  return handle(await fetch(`${API}/admin/shops/${shopId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  }))
}

// HAIST Admin — Users (/api/v1/admin/users, requires datium role)
export async function haistListUsers(token, { page = 1, size = 20, search, shop_id, is_active } = {}) {
  const params = new URLSearchParams({ page, size })
  if (search) params.set('search', search)
  if (shop_id) params.set('shop_id', shop_id)
  if (is_active !== undefined && is_active !== null) params.set('is_active', is_active)
  return handle(await fetch(`${API}/admin/users?${params}`, { headers: authHeaders(token) }))
}

export async function haistCreateUser(token, data) {
  return handle(await fetch(`${API}/admin/users`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function haistUpdateUser(token, userId, data) {
  return handle(await fetch(`${API}/admin/users/${userId}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  }))
}

export async function haistDeleteUser(token, userId) {
  return handle(await fetch(`${API}/admin/users/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  }))
}

export async function changePassword(token, newPassword) {
  return handle(await fetch(`${API}/auth/me`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ new_password: newPassword }),
  }))
}
